/**
 * Handles accumulation of message chunks into a single complete message.
 * This class is designed to handle one message at a time, accumulating
 * content chunks until the message is complete.
 */
export class MessageAccumulator {
    constructor() {
        this.currentMessage = null;
    }
    /**
     * Handles message started event by creating a new message with a temporary ID
     *
     * @param message - The message started message to handle
     * @returns The updated current message
     */
    handleMessageStarted(message) {
        const newMessage = {
            id: message.data.tempId,
            role: message.data.role,
            content: '',
        };
        this.currentMessage = newMessage;
        return Object.assign({}, newMessage);
    }
    /**
     * Accumulates content into the current message.
     * If no current message exists or the role changes, creates a new message.
     *
     * @param message - The stream data message to append
     * @returns The updated current message
     */
    append(message) {
        // Handle missing current message or role change
        if (!this.currentMessage ||
            message.data.role !== this.currentMessage.role) {
            this.currentMessage = {
                id: message.messageId,
                role: message.data.role,
                content: message.data.content,
                // Initialize metadata structure if tokens are present in the first chunk
                metadata: message.data.tokens
                    ? {
                        tokens: {
                            promptTokens: 0, // Assuming 0 initially
                            completionTokens: message.data.tokens.completion,
                            totalTokens: message.data.tokens.total,
                            model: '', // Assuming empty initially
                        },
                    }
                    : undefined, // Keep as undefined if no tokens in first chunk
            };
            // No need to process metadata further for the first chunk here,
            // as it was handled during initialization.
        }
        else {
            // Append content for subsequent chunks
            this.currentMessage.content += message.data.content;
            // Update metadata if present (only for subsequent chunks)
            if (message.data.tokens) {
                // Ensure metadata object exists before trying to update tokens
                if (!this.currentMessage.metadata) {
                    this.currentMessage.metadata = {};
                }
                // Update/overwrite the tokens part of metadata
                this.currentMessage.metadata.tokens = {
                    promptTokens: 0, // Still assuming 0
                    completionTokens: message.data.tokens.completion,
                    totalTokens: message.data.tokens.total,
                    model: '', // Still assuming empty
                };
            }
        }
        // Return a new message object for immutability
        const result = Object.assign({}, this.currentMessage);
        // Clear current message if this is the last chunk
        if (message.data.isLast) {
            this.currentMessage = null;
        }
        return result;
    }
    /**
     * Updates the current message's ID when receiving a completion event
     *
     * @param message - The message completed message to update
     * @returns The updated current message or undefined if the message is not the current message
     */
    updateMessageId(message) {
        if (!this.currentMessage ||
            this.currentMessage.id !== message.data.tempId) {
            return undefined;
        }
        // Set the tempIdToReplace to the current message id to share the info with the UI
        // to replace the temporary message with the final message
        this.currentMessage.tempIdToReplace = this.currentMessage.id;
        this.currentMessage.id = message.data.finalId;
        return Object.assign({}, this.currentMessage);
    }
    /**
     * Checks if the current message is complete
     *
     * @returns True if the current message is complete, false otherwise
     */
    isComplete() {
        return this.currentMessage === null;
    }
    /**
     * Gets the current message if it exists
     *
     * @returns The current message or null if no message exists
     */
    getCurrentMessage() {
        return this.currentMessage ? Object.assign({}, this.currentMessage) : null;
    }
    /**
     * Resets accumulator state
     */
    clear() {
        this.currentMessage = null;
    }
}
//# sourceMappingURL=message-accumulator.js.map